import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Stack, useTheme, TextField,
  Button, MenuItem, Alert, CircularProgress, Switch,
  FormControlLabel, Divider
} from '@mui/material';
import { useAuth } from '../util/auth';
import { getUser, updateUser } from '../util/db';
import { resetWalkthrough } from '../utils/socialActions';
import WebLayout from '../components/WebLayout';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const Profile = () => {
  const theme = useTheme();
  const auth = useAuth();
  const [name, setName] = useState('');
  const [homeState, setHomeState] = useState('');
  const [originalData, setOriginalData] = useState({ name: '', homeState: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [hasSeenWalkthrough, setHasSeenWalkthrough] = useState(false);

  useEffect(() => {
    const loadUserData = async () => {
      if (auth?.user?.uid) {
        try {
          const userData = await getUser(auth.user.uid);
          const userName = userData?.name || auth.user.displayName || '';
          const userHomeState = userData?.homeState || '';
          const userHasSeenWalkthrough = userData?.hasSeenWalkthrough || false;

          setName(userName);
          setHomeState(userHomeState);
          setHasSeenWalkthrough(userHasSeenWalkthrough);
          setOriginalData({ name: userName, homeState: userHomeState });
        } catch (error) {
          console.error('Error loading user data:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    loadUserData();
  }, [auth?.user]);

  const hasChanges = () => {
    return name !== originalData.name || homeState !== originalData.homeState;
  };

  const handleSave = async () => {
    if (!auth?.user?.uid) return;

    setSaving(true);
    setMessage(null);

    try {
      await updateUser(auth.user.uid, {
        name,
        homeState,
        email: auth.user.email
      });

      // Refresh user data in auth context
      await auth.refreshUserData();

      setOriginalData({ name, homeState });
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage({ type: 'error', text: 'Failed to update profile. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleWalkthroughToggle = async () => {
    if (!auth?.user?.uid) return;

    try {
      // Reset walkthrough in Firestore
      await resetWalkthrough(auth.user.uid);

      // Refresh user data in auth context so BonusWatcher can detect the change
      await auth.refreshUserData();

      setHasSeenWalkthrough(false);
      setMessage({
        type: 'success',
        text: 'Walkthrough enabled! It will start when you visit the Bonus Watcher page.'
      });
    } catch (error) {
      console.error('Error resetting walkthrough:', error);
      setMessage({
        type: 'error',
        text: 'Failed to enable walkthrough. Please try again.'
      });
    }
  };

  const getMemberSince = () => {
    if (auth?.userData?.createdAt) {
      return new Date(auth.userData.createdAt).toLocaleDateString();
    }
    if (auth?.user?.metadata?.creationTime) {
      return new Date(auth.user.metadata.creationTime).toLocaleDateString();
    }
    return 'N/A';
  };

  const content = (
    <Box
      sx={{
        minHeight: '100vh',
        background: theme.palette.background.default,
        p: 4,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          maxWidth: 600,
          mx: 'auto',
        }}
      >
        <Stack spacing={3}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Profile
          </Typography>

          {message && (
            <Alert severity={message.type} onClose={() => setMessage(null)}>
              {message.text}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={3}>
              <TextField
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
                variant="outlined"
              />

              <TextField
                label="Email"
                value={auth?.user?.email || ''}
                fullWidth
                variant="outlined"
                disabled
                helperText="Email cannot be changed"
              />

              <TextField
                select
                label="Home State"
                value={homeState}
                onChange={(e) => setHomeState(e.target.value)}
                fullWidth
                variant="outlined"
                helperText="Used to filter bonuses by your location"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {US_STATES.map((state) => (
                  <MenuItem key={state} value={state}>
                    {state}
                  </MenuItem>
                ))}
              </TextField>

              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Member Since
                </Typography>
                <Typography variant="subtitle1">
                  {getMemberSince()}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Preferences
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    backgroundColor: theme.palette.background.default,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Walkthrough Tour
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        {hasSeenWalkthrough
                          ? 'You\'ve completed the tour. Click to restart it.'
                          : 'The tour will start on your next visit to the dashboard.'}
                      </Typography>
                    </Box>
                    <Button
                      variant={hasSeenWalkthrough ? 'outlined' : 'contained'}
                      color="primary"
                      size="small"
                      onClick={handleWalkthroughToggle}
                      disabled={!hasSeenWalkthrough}
                      sx={{ ml: 2 }}
                    >
                      {hasSeenWalkthrough ? 'Restart Tour' : 'Enabled'}
                    </Button>
                  </Box>
                </Paper>
              </Box>

              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleSave}
                disabled={!hasChanges() || saving}
                fullWidth
              >
                {saving ? <CircularProgress size={24} /> : 'Save Changes'}
              </Button>
            </Stack>
          )}
        </Stack>
      </Paper>
    </Box>
  );

  return <WebLayout>{content}</WebLayout>;
};

export default Profile; 